/**
 * Invoice module print HTML only — do not reuse for SaaS BillDetail print
 * (`src/print/forms/invoiceBillPrint.js`).
 */
import { displayDateUS } from "../../utils/dateUtils";
import {
  computeInvoiceTotal,
  computeLineItemTotalFromRow,
  formatHoursDisplay,
  isHourlyVisitType,
  isLegacyClinicalVisitType,
  visitTypeDisplayLabel,
} from "./invoiceLineItem";
import { INVOICE_THEME_HEX } from "./invoiceTheme";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(amount) || 0);
}

function resolveLogoSrc(officeLogo) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (!officeLogo) return `${origin}/login-logo.png`;
  if (officeLogo.startsWith("http")) return officeLogo;
  return `${origin}${officeLogo.startsWith("/") ? officeLogo : `/${officeLogo}`}`;
}

function formatRateCell(item) {
  if (isHourlyVisitType(item.visitType) || isLegacyClinicalVisitType(item.visitType)) {
    return formatCurrency(item.rate);
  }
  return "—";
}

function buildSheetHtml(invoice, { companyLabel, logoSrc, formatDate }) {
  const items = invoice.invoiceData || [];
  const totalAmount = computeInvoiceTotal(items);

  const employeeName = escapeHtml(
    [invoice.employer?.firstName, invoice.employer?.lastName]
      .filter(Boolean)
      .join(" ") || "—",
  );
  const company = escapeHtml(companyLabel || invoice.companyName || "Company");
  const generatedOn = escapeHtml(
    formatDate(new Date()) || new Date().toLocaleDateString(),
  );
  const rows = items
    .map(
      (item, index) => `
        <tr>
          <td class="col-no">${index + 1}</td>
          <td class="col-name">${escapeHtml(item.patientName || "—")}</td>
          <td class="col-date">${escapeHtml(formatDate(item.visitDate) || "—")}</td>
          <td><span class="chip">${escapeHtml(visitTypeDisplayLabel(item.visitType) || "—")}</span></td>
          <td class="col-num">${escapeHtml(formatHoursDisplay(item.hours))}</td>
          <td class="col-rate">${escapeHtml(formatRateCell(item))}</td>
          <td class="col-rate">${escapeHtml(formatCurrency(computeLineItemTotalFromRow(item)))}</td>
        </tr>`,
    )
    .join("");

  const role = escapeHtml(invoice.role || "—");
  const visitCount = items.length;
  const billingPeriod = `${escapeHtml(formatDate(invoice.formDate) || "—")} – ${escapeHtml(formatDate(invoice.toDate) || "—")}`;
  const dueDate = escapeHtml(formatDate(invoice.dueDate) || "—");
  const notes = (invoice.notes || "").trim();
  const notesHtml = notes
    ? `<div class="meta-card meta-card-wide">
        <p class="meta-label">Notes</p>
        <p class="meta-value meta-notes">${escapeHtml(notes)}</p>
      </div>`
    : "";

  return `
  <section class="sheet">
    <div class="head">
      <div class="brand">
        <img src="${escapeHtml(logoSrc)}" alt="${company}" onerror="this.style.display='none'" />
        <div>
          <p class="brand-name">${company}</p>
          <p class="brand-sub">Home Health Services</p>
        </div>
      </div>
      <div class="head-right">
        <h1 class="doc-title">Invoice</h1>
        <span class="doc-no">#${escapeHtml(invoice.invoiceNumber)}</span>
        <p class="doc-date">Generated on ${generatedOn}</p>
      </div>
    </div>

    <div class="meta">
      <div class="meta-card">
        <p class="meta-label">Billed For</p>
        <p class="meta-value">${employeeName}</p>
      </div>
      <div class="meta-card">
        <p class="meta-label">Role</p>
        <p class="meta-value">${role}</p>
      </div>
      <div class="meta-card">
        <p class="meta-label">Billing Period</p>
        <p class="meta-value">${billingPeriod}</p>
      </div>
      <div class="meta-card">
        <p class="meta-label">Due Date</p>
        <p class="meta-value">${dueDate}</p>
      </div>
      <div class="meta-card">
        <p class="meta-label">Total Visits</p>
        <p class="meta-value">${visitCount}</p>
      </div>
      ${notesHtml}
    </div>

    <div class="items-wrap">
      <table class="items">
        <thead>
          <tr>
            <th class="col-no">#</th>
            <th>Patient Name</th>
            <th>Visit Date</th>
            <th>Visit Type</th>
            <th class="col-num">Hours</th>
            <th class="col-rate">Rate ($)</th>
            <th class="col-rate">Total ($)</th>
          </tr>
        </thead>
        <tbody>
          ${
            rows ||
            `<tr><td colspan="7" class="empty">No line items on this invoice</td></tr>`
          }
        </tbody>
      </table>
    </div>

    <div class="totals">
      <div class="totals-box">
        <div class="totals-row">
          <span>Visits billed</span><span>${visitCount}</span>
        </div>
        <div class="totals-row">
          <span>Subtotal</span><span>${escapeHtml(formatCurrency(totalAmount))}</span>
        </div>
        <div class="totals-grand">
          <span>Total Due</span>
          <span class="amount">${escapeHtml(formatCurrency(totalAmount))}</span>
        </div>
      </div>
    </div>

    <div class="foot">
      <span>Thank you for your business.</span>
      <span>${company} &middot; Invoice #${escapeHtml(invoice.invoiceNumber)}</span>
    </div>
  </section>`;
}

function buildDocumentCss() {
  const T = INVOICE_THEME_HEX;
  return `
    :root {
      --brand: ${T.strong};
      --brand-text: ${T.text};
      --brand-title: ${T.title};
      --brand-soft: ${T.soft};
      --brand-border: ${T.border};
      --ink: #0f172a;
      --muted: #64748b;
      --line: #e2e8f0;
    }
    * { box-sizing: border-box; }
    html, body { margin: 0; }
    body {
      font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      background: #f1f5f9;
      color: var(--ink);
      font-size: 13px;
      line-height: 1.55;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .toolbar {
      position: sticky; top: 0; z-index: 10;
      display: flex; align-items: center; justify-content: flex-end; gap: 8px;
      padding: 12px 24px;
      background: rgba(255,255,255,.9);
      backdrop-filter: blur(6px);
      border-bottom: 1px solid var(--line);
    }
    .btn {
      display: inline-flex; align-items: center; gap: 6px;
      height: 34px; padding: 0 14px;
      border-radius: 8px; border: 1px solid var(--brand);
      background: var(--brand); color: #fff;
      font-size: 13px; font-weight: 600; cursor: pointer;
      font-family: inherit;
    }
    .btn:hover { filter: brightness(.94); }
    .btn-ghost {
      background: #fff; color: var(--brand-text); border-color: var(--brand-border);
    }
    .btn-ghost:hover { background: var(--brand-soft); filter: none; }
    .sheet {
      max-width: 820px; margin: 24px auto 48px;
      background: #fff;
      border: 1px solid var(--brand-border);
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(15,23,42,.08);
    }
    .sheet + .sheet { margin-top: 32px; }
    .head {
      display: flex; align-items: flex-start; justify-content: space-between; gap: 24px;
      padding: 26px 32px;
      background: var(--brand-soft);
      border-bottom: 1px solid var(--brand-border);
    }
    .brand { display: flex; align-items: center; gap: 16px; min-width: 0; }
    .brand img { max-height: 52px; max-width: 150px; object-fit: contain; display: block; }
    .brand-name {
      margin: 0; font-size: 15px; font-weight: 700; color: var(--brand-title);
      letter-spacing: .02em;
    }
    .brand-sub { margin: 2px 0 0; font-size: 11px; color: var(--muted); }
    .head-right { text-align: right; white-space: nowrap; }
    .doc-title {
      margin: 0; font-size: 26px; font-weight: 800; letter-spacing: .16em;
      color: var(--brand-title); text-transform: uppercase;
    }
    .doc-no {
      display: inline-block; margin-top: 6px; padding: 3px 10px;
      border-radius: 999px; background: #fff; border: 1px solid var(--brand-border);
      color: var(--brand-text); font-size: 12px; font-weight: 700;
    }
    .doc-date { margin: 6px 0 0; font-size: 11px; color: var(--muted); }
    .meta {
      display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px; padding: 22px 32px 6px;
    }
    .meta-card {
      border: 1px solid var(--line); border-radius: 10px; padding: 12px 14px;
      background: #fff;
    }
    .meta-card-wide { grid-column: 1 / -1; }
    .meta-label {
      margin: 0 0 3px; font-size: 10px; font-weight: 700; letter-spacing: .09em;
      text-transform: uppercase; color: var(--brand-text);
    }
    .meta-value { margin: 0; font-size: 13px; font-weight: 600; color: var(--ink); }
    .meta-notes { font-weight: 500; white-space: pre-wrap; word-break: break-word; }
    .items-wrap { padding: 16px 32px 0; }
    table.items { width: 100%; border-collapse: collapse; }
    table.items thead th {
      background: var(--brand-soft);
      color: var(--brand-text);
      text-align: left; font-size: 10px; font-weight: 700;
      letter-spacing: .08em; text-transform: uppercase;
      padding: 10px 12px;
      border-top: 1px solid var(--brand-border);
      border-bottom: 1px solid var(--brand-border);
    }
    table.items tbody td {
      padding: 10px 12px; border-bottom: 1px solid var(--line); vertical-align: middle;
    }
    table.items tbody tr:last-child td { border-bottom: 1px solid var(--brand-border); }
    .col-no { width: 40px; color: var(--muted); text-align: center; }
    .col-name { font-weight: 600; }
    .col-date { white-space: nowrap; font-variant-numeric: tabular-nums; }
    .col-num, .col-rate {
      text-align: right; white-space: nowrap; font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
    .col-num { font-weight: 500; color: var(--muted); }
    .chip {
      display: inline-block; padding: 2px 8px; border-radius: 999px;
      background: var(--brand-soft); border: 1px solid var(--brand-border);
      color: var(--brand-text); font-size: 11px; font-weight: 600;
    }
    .empty { text-align: center; padding: 22px; color: var(--muted); }
    .totals { display: flex; justify-content: flex-end; padding: 16px 32px 0; }
    .totals-box { width: 280px; }
    .totals-row {
      display: flex; justify-content: space-between; gap: 16px;
      padding: 7px 0; font-size: 12px; color: var(--muted);
    }
    .totals-row span:last-child { color: var(--ink); font-weight: 600; font-variant-numeric: tabular-nums; }
    .totals-grand {
      display: flex; justify-content: space-between; gap: 16px; align-items: baseline;
      margin-top: 6px; padding: 12px 0 0;
      border-top: 1px solid var(--brand-border);
      color: var(--brand-title); font-size: 14px; font-weight: 700;
    }
    .totals-grand .amount {
      color: var(--brand-text); font-size: 18px; font-variant-numeric: tabular-nums;
    }
    .foot {
      margin-top: 26px; padding: 16px 32px 26px;
      border-top: 1px solid var(--line);
      display: flex; justify-content: space-between; gap: 16px;
      font-size: 11px; color: var(--muted);
    }
    @page {
      size: letter portrait;
      margin: 0.5in;
    }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
      }
      .toolbar { display: none !important; }
      .sheet {
        max-width: none;
        width: auto;
        margin: 0 !important;
        border: none;
        border-radius: 0;
        box-shadow: none;
        overflow: visible;
        page-break-after: always;
        break-after: page;
      }
      .sheet:last-of-type {
        page-break-after: auto;
        break-after: auto;
      }
      /* Keep the same internal padding as the on-screen preview (WYSIWYG). */
      table.items thead { display: table-header-group; }
      table.items tbody tr { page-break-inside: avoid; break-inside: avoid; }
      .totals, .foot { page-break-inside: avoid; break-inside: avoid; }
    }
  `;
}

/**
 * Build a complete printable HTML document for one or many invoices.
 * Multiple invoices → one document; each invoice starts on a new printed page.
 */
export function buildInvoiceModulePrintHtml(invoices, options = {}) {
  const list = (Array.isArray(invoices) ? invoices : [invoices]).filter(Boolean);
  if (!list.length) return "";

  const formatDate = options.formatDate || displayDateUS;
  const companyLabel = options.companyName || "";
  const logoSrc = resolveLogoSrc(options.officeLogo);

  const sheets = list
    .map((inv) =>
      buildSheetHtml(inv, { companyLabel, logoSrc, formatDate }),
    )
    .join("\n");

  const title =
    list.length === 1
      ? `Invoice #${escapeHtml(list[0].invoiceNumber)}`
      : `${list.length} Invoices`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${title}</title>
  <style>${buildDocumentCss()}</style>
</head>
<body>
  <div class="toolbar">
    <button type="button" class="btn-ghost btn" onclick="window.close()">Close</button>
    <button type="button" class="btn" id="invoice-print-btn">
      ${list.length > 1 ? `Print ${list.length} Invoices` : "Print Invoice"}
    </button>
  </div>
  ${sheets}
  <script>
    (function () {
      function runPrint() {
        try {
          window.focus();
          window.print();
        } catch (e) {}
      }
      function waitForImages(root) {
        var imgs = Array.prototype.slice.call(root.querySelectorAll("img"));
        var pending = imgs.filter(function (img) { return !img.complete; });
        if (!pending.length) return Promise.resolve();
        return Promise.all(
          pending.map(function (img) {
            return new Promise(function (resolve) {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            });
          }),
        );
      }
      var btn = document.getElementById("invoice-print-btn");
      if (btn) {
        btn.addEventListener("click", function () {
          btn.disabled = true;
          Promise.all([
            document.fonts && document.fonts.ready
              ? document.fonts.ready.catch(function () {})
              : Promise.resolve(),
            waitForImages(document),
          ]).then(function () {
            requestAnimationFrame(function () {
              requestAnimationFrame(runPrint);
            });
          }).finally(function () {
            btn.disabled = false;
          });
        });
      }
    })();
  </script>
</body>
</html>`;
}

/** Open one preview tab with the given invoice(s). */
export function openInvoiceModulePrint(invoices, options = {}) {
  const html = buildInvoiceModulePrintHtml(invoices, options);
  if (!html) return false;
  const printWindow = window.open("", "_blank");
  if (!printWindow || !printWindow.document) return false;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}
